import { AccountReportView } from '@/domain/reports/report'
import { reportsApi, CreateReportViewPayload, UpdateReportViewPayload } from '@/rest/modules/reports-api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../use-toast'

export const reportKeys = {
  all: ['reports'] as const,
  lists: () => [...reportKeys.all, 'list'] as const,
  detail: (id: string) => [...reportKeys.all, 'detail', id] as const,
}

export const useReportViews = () =>
  useQuery<AccountReportView[]>({
    queryKey: reportKeys.lists(),
    queryFn: () => reportsApi.getAll(),
  })

export const useReportView = (id: string) =>
  useQuery<AccountReportView>({
    queryKey: reportKeys.detail(id),
    queryFn: () => reportsApi.getById(id),
    enabled: !!id,
  })

export const useCreateReportView = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (payload: CreateReportViewPayload) => reportsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      toast({ title: 'Éxito', description: 'Vista creada correctamente', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo crear la vista', variant: 'destructive' })
    },
  })
}

export const useUpdateReportView = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReportViewPayload }) =>
      reportsApi.update(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(variables.id) })
      toast({ title: 'Éxito', description: 'Vista actualizada correctamente', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar la vista', variant: 'destructive' })
    },
  })
}

export const useDeleteReportView = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => reportsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      toast({ title: 'Éxito', description: 'Vista eliminada correctamente', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo eliminar la vista', variant: 'destructive' })
    },
  })
}

